import _Array$isArray from '@babel/runtime-corejs3/core-js-stable/array/is-array';
import _mapInstanceProperty from '@babel/runtime-corejs3/core-js-stable/instance/map';
import _extends from '@babel/runtime-corejs3/helpers/esm/extends';
import _slicedToArray from '@babel/runtime-corejs3/helpers/esm/slicedToArray';
import _objectWithoutProperties from '@babel/runtime-corejs3/helpers/esm/objectWithoutProperties';
import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import bemCnFast from '@webteam/bem-cn-fast';
import Tag from '@webteam/tag';
import { withTheme } from '@webteam/ui-contexts';
import { DownIcon, LoadingIcon, CloseIcon } from '@webteam/icons';
import { optionType } from '../common-prop-types.js';
import Foldable from './foldable.js';
import localNames from './trigger.module.pcss.js';

var bemCn = bemCnFast('wt-select', localNames);
var TIMEOUT_BETWEEN_EVENTS = 100; //100ms
// eslint-disable-next-line complexity

function SelectTrigger(props) {
  var size = props.size,
      theme = props.theme,
      className = props.className,
      triggerRef = props.triggerRef,
      isOpen = props.isOpen,
      _props$selected = props.selected,
      selected = _props$selected === void 0 ? null : _props$selected;
      props.placeholder;
      props.isClearable;
      props.isLoading;
      var isDisabled = props.isDisabled;
      props.isSingleLine;
      props.searchInput;
      var isSearchable = props.isSearchable;
      props.onClearClick;
      props.onItemClearClick;
      var onSearchChange = props.onSearchChange,
      error = props.error,
      restProps = _objectWithoutProperties(props, ["size", "theme", "className", "triggerRef", "isOpen", "selected", "placeholder", "isClearable", "isLoading", "isDisabled", "isSingleLine", "searchInput", "isSearchable", "onClearClick", "onItemClearClick", "onSearchChange", "error"]);

  var inputRef = useRef(null);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      searchFocused = _useState2[0],
      setSearchFocused = _useState2[1];

  var _useState3 = useState(0),
      _useState4 = _slicedToArray(_useState3, 2),
      lastMouseDownTimestamp = _useState4[0],
      setLastMouseDownTimestamp = _useState4[1];

  var isMulti = selected ? Boolean(selected.length && selected.length > 0) : false;

  function handleChange(event) {
    var newValue = event.target.value;
    onSearchChange(newValue);
  }

  function handleBlur() {
    if (isSearchable && inputRef.current) {
      setSearchFocused(false);
      inputRef.current.blur();
    }
  }

  function handleFocus(e) {
    if (e.timeStamp - lastMouseDownTimestamp > TIMEOUT_BETWEEN_EVENTS && isSearchable && inputRef.current) {
      setSearchFocused(true);
      inputRef.current.focus();
    }
  }

  function handleMouseDown(e) {
    setLastMouseDownTimestamp(e.timeStamp);
  }

  return React.createElement("div", _extends({
    tabIndex: isSearchable ? -1 : 0,
    className: cn(bemCn({
      size: size,
      theme: theme,
      isMulti: isMulti,
      searchFocused: searchFocused,
      open: isOpen,
      disabled: isDisabled,
      error: Boolean(error)
    }), className),
    ref: triggerRef,
    "data-test": "select"
  }, restProps), isMulti ? renderMultiselectOptions(props) : renderSelectedOption(props, inputRef, handleBlur, handleFocus, handleMouseDown, handleChange), React.createElement("div", {
    className: bemCn('icons-wrapper', {
      size: size
    })
  }, renderClearOrLoading(props), React.createElement(DownIcon, {
    size: size,
    className: bemCn('toggle', {
      open: isOpen
    }),
    "data-test": "down-icon"
  })));
}

function renderMultiselectOptions(_ref) {
  var isSingleLine = _ref.isSingleLine,
      selected = _ref.selected,
      size = _ref.size,
      isDisabled = _ref.isDisabled,
      onItemClearClick = _ref.onItemClearClick;
  var selectedOptions = renderSelectedItems(selected, size, isDisabled, onItemClearClick);
  return React.createElement("div", {
    className: bemCn('multi'),
    "data-test": "multi"
  }, isSingleLine ? wrapWithFoldable(selectedOptions, size, isDisabled) : selectedOptions);
}

function wrapWithFoldable(selectedOptions, size, isDisabled) {
  return React.createElement(Foldable, {
    renderHiddenCounter: function renderHiddenCounter(n, ref) {
      return React.createElement(Tag, {
        className: bemCn('tag'),
        key: "+",
        ref: ref,
        size: {
          m: 's',
          s: 'xs'
        }[size],
        disabled: isDisabled
      }, "+".concat(n));
    },
    gap: {
      m: 5,
      s: 4
    }[size]
  }, selectedOptions);
}

function renderSelectedItems(selected, size, isDisabled, onItemClearClick) {
  return _mapInstanceProperty(selected).call(selected, function (s) {
    var key = s.value ? s.value : s.label;
    return React.createElement(Tag, {
      key: key,
      "data-test": "selected-tag-".concat(key),
      className: bemCn('tag'),
      isRemovable: true,
      disabled: isDisabled,
      onRemoveClick: function onRemoveClick() {
        return onItemClearClick(s);
      },
      size: {
        m: 's',
        s: 'xs'
      }[size]
    }, s.label);
  });
}

function isSelectedEmpty(selected) {
  if (_Array$isArray(selected)) {
    return selected.length === 0;
  } else {
    return !selected;
  }
}

function renderSelectedOption(props, inputRef, handleBlur, handleFocus, handleMouseDown, handleChange) {
  /* eslint-disable react/prop-types */
  var selected = props.selected,
      _props$searchInput = props.searchInput,
      searchInput = _props$searchInput === void 0 ? '' : _props$searchInput,
      _props$placeholder = props.placeholder,
      placeholder = _props$placeholder === void 0 ? '' : _props$placeholder,
      isSearchable = props.isSearchable,
      isDisabled = props.isDisabled,
      size = props.size;
  return React.createElement("div", {
    className: bemCn('label-wrapper')
  }, React.createElement("div", {
    className: bemCn('label', {
      notSelected: isSelectedEmpty(selected)
    })
  }, searchInput === '' ? selected && selected.label ? selected.label : placeholder : '', isSearchable && !isDisabled && React.createElement("input", {
    ref: inputRef,
    tabIndex: isSearchable ? 0 : -1,
    onBlur: handleBlur,
    onFocus: handleFocus,
    onMouseDown: handleMouseDown,
    className: bemCn('search-input', {
      size: size
    }),
    autoCapitalize: "none",
    autoComplete: "off",
    autoCorrect: "off",
    spellCheck: "false",
    type: "text",
    value: searchInput,
    onChange: handleChange,
    "data-test": "search-input"
  })));
}

function renderClearOrLoading(props) {
  var isClearable = props.isClearable,
      isLoading = props.isLoading,
      isDisabled = props.isDisabled,
      onClearClick = props.onClearClick,
      selected = props.selected,
      size = props.size;

  if (isLoading) {
    return React.createElement(LoadingIcon, {
      style: {
        pointerEvents: 'none'
      },
      size: size,
      className: bemCn('icon', {
        size: size
      }),
      "data-test": "loading-icon"
    });
  } else if (isClearable && !isDisabled && !isSelectedEmpty(selected)) {
    return React.createElement(CloseIcon, {
      size: size,
      className: bemCn('icon', {
        size: size
      }),
      "data-test": "clear-icon",
      onClick: function onClick(e) {
        e.stopPropagation();
        onClearClick();
      }
    });
  } else {
    return null;
  }
}

SelectTrigger.propTypes = {
  size: PropTypes.oneOf(['m', 's', 'xs']),
  theme: PropTypes.oneOf(['light', 'dark']),
  className: PropTypes.string,
  isOpen: PropTypes.bool,
  selected: PropTypes.oneOfType([optionType, PropTypes.arrayOf(optionType)]),
  placeholder: PropTypes.string,
  isClearable: PropTypes.bool,
  isLoading: PropTypes.bool,
  isDisabled: PropTypes.bool,
  isSearchable: PropTypes.bool,
  isSingleLine: PropTypes.bool,
  onClearClick: PropTypes.func,
  onItemClearClick: PropTypes.func,
  onSearchChange: PropTypes.func,
  error: PropTypes.bool,
  triggerRef: PropTypes.oneOfType([PropTypes.func, PropTypes.shape({
    current: PropTypes.any
  })]),
  searchInput: PropTypes.string
};
var SelectTrigger$1 = withTheme(SelectTrigger);

export { SelectTrigger$1 as default };
